import { useRef, useState } from "react"
import Chip from "@mui/material/Chip"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import colors from "assets/theme/base/colors"

// Loose but sufficient client-side check — the backend is the real validator.
const EMAIL_REGEX = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/

// Tag/chip email input: type an address, hit comma / space / Enter (or blur)
// to confirm it into a badge; click a badge to pop it back into edit text at
// the end of the field, or hit its "x" to remove it outright. Backing value
// stays the same comma-separated string the API already expects
// (`approverRef` for approverType "emails") — this only changes how it's
// edited, not the wire format.
const EmailChipInput = ({ value, onChange, placeholder, error, invalidEmails = [] }) => {
  const isInvalidChip = (email) =>
    invalidEmails.some((v) => v.toLowerCase() === email.toLowerCase())
  const chips = (value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
  const [draft, setDraft] = useState("")
  const [draftInvalid, setDraftInvalid] = useState(false)
  const inputRef = useRef(null)

  const commitChips = (newlyValid) => {
    if (newlyValid.length === 0) return
    const merged = [...chips]
    newlyValid.forEach((v) => {
      if (!merged.includes(v)) merged.push(v)
    })
    onChange(merged.join(","))
  }

  // Splits the current draft on commas, chips every segment that's a valid
  // email, and leaves whatever doesn't validate sitting in the field so nothing
  // typed is ever silently discarded.
  const tryCommitDraft = (text) => {
    const parts = text
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
    const valid = parts.filter((p) => EMAIL_REGEX.test(p))
    const invalid = parts.filter((p) => !EMAIL_REGEX.test(p))
    commitChips(valid)
    const remaining = invalid.join(", ")
    setDraft(remaining)
    setDraftInvalid(invalid.length > 0)
  }

  const handleChange = (e) => {
    const raw = e.target.value
    if (/[,\s]$/.test(raw)) {
      tryCommitDraft(raw)
      return
    }
    setDraft(raw)
    setDraftInvalid(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      tryCommitDraft(draft)
    } else if (e.key === "Backspace" && !draft && chips.length > 0) {
      // Edit the last chip instead of just deleting it silently.
      const last = chips[chips.length - 1]
      onChange(chips.slice(0, -1).join(","))
      setDraft(last)
    }
  }

  const handleBlur = () => {
    if (draft.trim()) tryCommitDraft(draft)
  }

  const removeChip = (idx) => {
    onChange(chips.filter((_, i) => i !== idx).join(","))
  }

  // Click the label (not the delete "x"): pop it back into the text so the
  // admin can fix a typo, appended after whatever they're mid-typing.
  const editChip = (idx) => {
    const email = chips[idx]
    onChange(chips.filter((_, i) => i !== idx).join(","))
    setDraft((prev) => (prev ? `${prev} ${email}` : email))
    setDraftInvalid(false)
    inputRef.current?.focus()
  }

  return (
    <MDBox
      onClick={() => inputRef.current?.focus()}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.75,
        minHeight: "2.5rem",
        p: 0.75,
        borderRadius: "0.5rem",
        border: "1px solid",
        borderColor: draftInvalid || error ? colors.error.main : colors.grey[300],
        cursor: "text",
        transition: "border-color 0.15s ease",
        "&:focus-within": {
          borderColor: draftInvalid || error ? colors.error.main : colors.info.main,
        },
      }}
    >
      {chips.map((email, idx) => (
        <Chip
          key={`${email}-${idx}`}
          label={email}
          size="small"
          onClick={() => editChip(idx)}
          onDelete={() => removeChip(idx)}
          sx={{
            cursor: "pointer",
            maxWidth: "100%",
            "& .MuiChip-label": { fontSize: "0.875rem" },
            ...(isInvalidChip(email) && {
              border: "1px solid",
              borderColor: colors.error.main,
              color: colors.error.main,
            }),
          }}
        />
      ))}
      <MDBox
        component="input"
        inputRef={inputRef}
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={chips.length === 0 ? placeholder : ""}
        sx={{
          flex: "1 1 8rem",
          minWidth: "8rem",
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: "0.875rem",
          fontFamily: "inherit",
          padding: "0.25rem 0.25rem",
        }}
      />
      {draftInvalid && (
        <MDTypography
          variant="caption"
          color="error"
          sx={{ flexBasis: "100%", pl: 0.5 }}
        >
          <Icon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.25 }}>
            error
          </Icon>
          Not a valid email
        </MDTypography>
      )}
    </MDBox>
  )
}

export default EmailChipInput
